import { startTransition, useCallback, useEffect, useEffectEvent, useMemo, useState } from "react";

import { get } from "@/utils/get";

import {
  doesStartsWith,
  isEmail,
  isIs,
  isLength,
  isMax,
  isMin,
  isNot,
  isNumber,
  isObject,
  isOutside,
  isPattern,
  isRequired,
  isString,
  isTruthy,
  isUrl,
} from "./rules";

function flattenObj(validationRuleSet) {
  const result = {};

  if (validationRuleSet) {
    Object.keys(validationRuleSet).map((key) => {
      if (validationRuleSet[key].rules[0].validator === "object") {
        result[key] = validationRuleSet[key];

        if (validationRuleSet[key].rules[0].config) {
          const temp = flattenObj(validationRuleSet[key].rules[0].config.rules);

          for (const nestedKey in temp) {
            result[key + "." + nestedKey] = temp[nestedKey];
          }
        }
      } else {
        result[key] = validationRuleSet[key];
      }
    });
  }

  return result;
}

export default function useEndorse(
  endorsable,
  validationRules = () => ({}),
  touched,
  showAllErrors,
  internalSaveErrors = {},
) {
  const [errors, setErrors] = useState({});
  const isValid = useMemo(() => Object.keys(errors).length === 0, [errors]);

  // Core functions
  const publishError = useCallback((shouldNotPublish, validationError) => {
    if (shouldNotPublish) {
      return;
    }

    setErrors((prevErrors) => ({
      ...prevErrors,
      [validationError.key]: prevErrors[validationError.key]
        ? [validationError.error, ...prevErrors[validationError.key]]
        : [validationError.error],
    }));
  }, []);

  const allErrors = useMemo(() => {
    // Combine Endorse and API errors
    const uniqueKeys = [...new Set([...Object.keys(errors), ...Object.keys(internalSaveErrors)])];
    const _errors = uniqueKeys.reduce(
      (newList, curKey) => ({
        ...newList,
        [curKey]: [...(errors?.[curKey] ?? []), ...(internalSaveErrors?.[curKey] ?? [])],
      }),
      {},
    );

    return _errors;
  }, [errors, internalSaveErrors]);

  const visibleErrors = useMemo(() => {
    if (showAllErrors || touched === undefined) {
      return allErrors;
    }

    return Object.keys(allErrors)
      .filter((key) => touched.indexOf(key) !== -1)
      .reduce((newObj, currKey) => ((newObj[currKey] = allErrors[currKey]), newObj), {});
  }, [allErrors, showAllErrors, touched]);

  // Helper functions
  const ruleConditionIsFalseOrKeyIsMissing = useCallback(
    (key, options) => {
      // If the current condition is false or the key isn't in the object, ignore the validator
      if (
        options?.condition === false ||
        get(key, endorsable) === "" ||
        get(key, endorsable) === null ||
        get(key, endorsable) === undefined
      ) {
        return true;
      }

      return false;
    },
    [endorsable],
  );

  const getPublishTarget = useCallback((key, options) => {
    return options?.publishAt ?? key;
  }, []);

  /* Value Validators
   * The following validators validate only one value and can only publish a single error.
   */
  // Check if the given value is present
  const requiredRuleValidator = useCallback(
    (key, options) => {
      if (options?.condition === false) return;

      publishError(isRequired(get(key, endorsable)), {
        key: getPublishTarget(key, options),
        error: options?.message ?? "This field is required.",
      });
    },
    [endorsable, publishError, getPublishTarget],
  );

  // Check if the given value is of the type 'string'
  const stringRuleValidator = useCallback(
    (key, options) => {
      if (ruleConditionIsFalseOrKeyIsMissing(key, options)) return;

      publishError(isString(get(key, endorsable)), {
        key: getPublishTarget(key, options),
        error: options?.message ?? "This field must be a string.",
      });
    },
    [endorsable, publishError, getPublishTarget, ruleConditionIsFalseOrKeyIsMissing],
  );

  // Check if the given value is of the type 'number'
  const numberRuleValidator = useCallback(
    (key, options) => {
      if (ruleConditionIsFalseOrKeyIsMissing(key, options)) return;

      publishError(isNumber(get(key, endorsable)), {
        key: getPublishTarget(key, options),
        error: options?.message ?? "This field must be a number.",
      });
    },
    [endorsable, publishError, getPublishTarget, ruleConditionIsFalseOrKeyIsMissing],
  );

  // Check if the given value is of the type 'object'
  const objectRuleValidator = useCallback(
    (key, options) => {
      if (ruleConditionIsFalseOrKeyIsMissing(key, options)) return;

      publishError(isObject(get(key, endorsable)), {
        key: getPublishTarget(key, options),
        error: options?.message ?? "This field must be an object.",
      });
    },
    [endorsable, publishError, getPublishTarget, ruleConditionIsFalseOrKeyIsMissing],
  );

  // Check if the given value is equal, higher or longer than the min
  const minRuleValidator = useCallback(
    (key, config, options) => {
      if (ruleConditionIsFalseOrKeyIsMissing(key, options)) return;

      const value = get(key, endorsable);

      publishError(isMin(value, config.min), {
        key: getPublishTarget(key, options),
        error: options?.message
          ? options?.message
          : isNumber(value)
            ? `This field must be at least ${config.min}.`
            : isString(value)
              ? `This field must be at least ${config.min} characters.`
              : `This field must be at least ${config.min}.`,
      });
    },
    [endorsable, publishError, getPublishTarget, ruleConditionIsFalseOrKeyIsMissing],
  );

  // Check if the given value is equal, lower or shorter than the max
  const maxRuleValidator = useCallback(
    (key, config, options) => {
      if (ruleConditionIsFalseOrKeyIsMissing(key, options)) return;

      const value = get(key, endorsable);

      publishError(isMax(value, config.max), {
        key: getPublishTarget(key, options),
        error: options?.message
          ? options?.message
          : isNumber(value)
            ? `This field must be at most ${config.max}.`
            : isString(value)
              ? `This field must be at most ${config.max} characters.`
              : `This field must be at most ${config.max}.`,
      });
    },
    [endorsable, publishError, getPublishTarget, ruleConditionIsFalseOrKeyIsMissing],
  );

  // Check if the given value is equal, lower or shorter than the max
  const lengthRuleValidator = useCallback(
    (key, config, options) => {
      if (ruleConditionIsFalseOrKeyIsMissing(key, options)) return;

      const value = get(key, endorsable);

      publishError(isLength(value, config.range), {
        key: getPublishTarget(key, options),
        error: options?.message
          ? options?.message
          : !config.range[0]
            ? `This field must be at most ${config.max} characters.`
            : !config.range[1]
              ? `This field must be at least ${config.max} characters.`
              : `This field must be between ${config.min} and ${config.max} characters.`,
      });
    },
    [endorsable, publishError, getPublishTarget, ruleConditionIsFalseOrKeyIsMissing],
  );

  // Check if the given value is equal, lower or shorter than the max
  const outsideRuleValidator = useCallback(
    (key, config, options) => {
      if (ruleConditionIsFalseOrKeyIsMissing(key, options)) return;

      const value = get(key, endorsable);

      publishError(isOutside(value, config.range), {
        key: getPublishTarget(key, options),
        error: options?.message ?? `This field must be outside ${config.range?.[0]} and ${config.range?.[1]}.`,
      });
    },
    [endorsable, publishError, getPublishTarget, ruleConditionIsFalseOrKeyIsMissing],
  );

  // Check if the given value is the given value
  const isRuleValidator = useCallback(
    (key, config, options) => {
      if (ruleConditionIsFalseOrKeyIsMissing(key, options)) return;

      publishError(isIs(get(key, endorsable), config.is), {
        key: getPublishTarget(key, options),
        error: options?.message ?? `This field must match ${String(key)}.`,
      });
    },
    [endorsable, publishError, getPublishTarget, ruleConditionIsFalseOrKeyIsMissing],
  );

  // Check if the given value is not the given value
  const notRuleValidator = useCallback(
    (key, config, options) => {
      if (ruleConditionIsFalseOrKeyIsMissing(key, options)) return;

      publishError(isNot(get(key, endorsable), config.not), {
        key: getPublishTarget(key, options),
        error: options?.message ?? `This field cannot be ${config.not}.`,
      });
    },
    [endorsable, publishError, getPublishTarget, ruleConditionIsFalseOrKeyIsMissing],
  );

  // Check if the given string is an email
  const emailRuleValidator = useCallback(
    (key, options) => {
      if (ruleConditionIsFalseOrKeyIsMissing(key, options)) return;

      publishError(isEmail(get(key, endorsable)), {
        key: getPublishTarget(key, options),
        error: options?.message ?? `This is an invalid email address.`,
      });
    },
    [endorsable, publishError, getPublishTarget, ruleConditionIsFalseOrKeyIsMissing],
  );

  // Check if the given string is an url
  const urlRuleValidator = useCallback(
    (key, options) => {
      if (ruleConditionIsFalseOrKeyIsMissing(key, options)) return;

      publishError(isUrl(get(key, endorsable)), {
        key: getPublishTarget(key, options),
        error: options?.message ?? "This is an invalid URL.",
      });
    },
    [endorsable, publishError, getPublishTarget, ruleConditionIsFalseOrKeyIsMissing],
  );

  // Check if the given key's value starts with the given value
  const startsWithRuleValidator = useCallback(
    (key, config, options) => {
      if (ruleConditionIsFalseOrKeyIsMissing(key, options)) return;

      publishError(doesStartsWith(get(key, endorsable), config.startsWith), {
        key: getPublishTarget(key, options),
        error: options?.message ?? `This field must start with ${config.startsWith}.`,
      });
    },
    [endorsable, publishError, getPublishTarget, ruleConditionIsFalseOrKeyIsMissing],
  );

  // Check if the given key's value starts with the given value
  const truthyRuleValidator = useCallback(
    (key, config, options) => {
      if (ruleConditionIsFalseOrKeyIsMissing(key, options)) return;

      publishError(isTruthy(config.truth), {
        key: getPublishTarget(key, options),
        error: options?.message ?? `This field must be true.`,
      });
    },
    [getPublishTarget, publishError, ruleConditionIsFalseOrKeyIsMissing],
  );

  // Check if the given key's value starts with the given value
  const patternRuleValidator = useCallback(
    (key, config, options) => {
      if (ruleConditionIsFalseOrKeyIsMissing(key, options)) return;

      publishError(isPattern(get(key, endorsable), config.pattern, options.inverse), {
        key: getPublishTarget(key, options),
        error: options?.message ?? `This field is an invalid format.`,
      });
    },
    [endorsable, getPublishTarget, publishError, ruleConditionIsFalseOrKeyIsMissing],
  );

  const getValidationRuleSet = useEffectEvent(() => flattenObj(validationRules(endorsable)));

  // UseEndorse
  useEffect(() => {
    startTransition(() => {
      setErrors({});
    });

    const validationRuleSet = getValidationRuleSet();

    Object.keys(validationRuleSet).map((key) => {
      // Loop over each key's rules
      validationRuleSet[key].rules.map((rule) => {
        switch (rule.validator) {
          case "required":
            requiredRuleValidator(key, rule.options);
            break;
          case "string":
            stringRuleValidator(key, rule.options);
            break;
          case "number":
            numberRuleValidator(key, rule.options);
            break;
          case "object":
            objectRuleValidator(key, rule.options);
            break;
          case "min":
            minRuleValidator(key, rule?.config, rule.options);
            break;
          case "max":
            maxRuleValidator(key, rule?.config, rule.options);
            break;
          case "length":
            lengthRuleValidator(key, rule?.config, rule.options);
            break;
          case "outside":
            outsideRuleValidator(key, rule?.config, rule.options);
            break;
          case "is":
            isRuleValidator(key, rule?.config, rule.options);
            break;
          case "not":
            notRuleValidator(key, rule?.config, rule.options);
            break;
          case "email":
            emailRuleValidator(key, rule.options);
            break;
          case "url":
            urlRuleValidator(key, rule.options);
            break;
          case "startsWith":
            startsWithRuleValidator(key, rule?.config, rule.options);
            break;
          case "truthy":
            truthyRuleValidator(key, rule?.config, rule.options);
            break;
          case "pattern":
            patternRuleValidator(key, rule?.config, rule.options);
            break;
        }
      });
    });
  }, [
    endorsable,
    requiredRuleValidator,
    stringRuleValidator,
    numberRuleValidator,
    minRuleValidator,
    maxRuleValidator,
    lengthRuleValidator,
    outsideRuleValidator,
    emailRuleValidator,
    urlRuleValidator,
    isRuleValidator,
    notRuleValidator,
    startsWithRuleValidator,
    objectRuleValidator,
    truthyRuleValidator,
    patternRuleValidator,
  ]);

  return { isValid, errors: visibleErrors, allErrors };
}
